import { Box, Stack } from "@mui/system";
import React, { useState } from "react";
import { Button, ButtonGroup, Grid, Typography } from "@mui/material";
import CustomAppBar from "../../../components/CustomAppBar";
import { useLocation, useNavigate } from "react-router";
import DailyReservationForm from "../../../components/forms/resservationForms/dailyReservationForm";
import MonthlyReservationForm from "../../../components/forms/resservationForms/monthlyReservationForm";
import MenuBar from "../../menuBar";
import { useResponsive } from "../../../hooks/useResponsive";
import { useEffect } from "react";
import storePersist, { localConstant } from "../../../services/localStorage";
import { enqueueSnackbar } from "notistack";
import SnackbarConfig from "../../../utils/snackbarConfig";
import "./styles.css";
import { MOMENT_CONSTANTS, convertYardToUtc } from "../../../utils/momentUtils";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { availabilitySummaryRequest } from "../../../redux/reservations/action";
import { useDispatch, useSelector } from "react-redux";
import CustomLoader from "../../../components/CustomLoader";
import { truncateLocationName } from "../../../utils";

import { getVehicles } from "../../../redux/vehicle/action";

const MakeReservationPage = React.forwardRef(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, availabilitySummaryData } = useSelector(
    (state) => state.reservationReducer,
  );

  const [reservationType, setReservationType] = useState("DAILY");
  const lgUp = useResponsive("up", "lg");
  const smDown = useResponsive("down", "sm");
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const locationId = location.state?.location_id;
  const state = location.state;

  const isVacanciesAvailable = (vehicle = []) => {
    for (let itm of vehicle) {
      if (itm.count > 0) {
        return true;
      }
    }
    return false;
  };

  const formSubmit = (values) => {
    const formSummary = {
      ...values,
      fromDate: moment(values?.fromDate)?.format(
        MOMENT_CONSTANTS.date.monthDayYear,
      ),
      time:
        values?.time ||
        (reservationType === "MONTHLY" &&
        !moment(values?.fromDate).isSame(moment(), "day")
          ? "00:00"
          : moment
              .tz(new Date(), state?.timezone)
              .add(1, "hour")
              .format(MOMENT_CONSTANTS.time["24Hours"])),
      ...{
        locationId: locationId,
        reservationType: reservationType,
        reservationState: state,
      },
    };

    const currentDate = moment(formSummary?.fromDate);
    const futureDate =
      formSummary?.reservationType === "DAILY"
        ? moment(currentDate).add(formSummary?.numOfDays, "days")
        : currentDate.clone().add(1, "month").endOf("month");
    const hours = formSummary?.time?.split(":")?.[0];
    const availabilityParam = {
      locationId: formSummary?.locationId,
      reservationType: formSummary?.reservationType,
      fromDate: convertYardToUtc({
        date: moment(formSummary?.fromDate).set({ hour: hours }),
        timeZone: formSummary?.reservationState?.timezone,
      }),

      toDate: convertYardToUtc({
        date: futureDate.set({ hour: 23, minute: 59, second: 59 }),
        timeZone: formSummary?.reservationState?.timezone,
      }),
    };
    dispatch(
      availabilitySummaryRequest(availabilityParam, (response) => {
        const { availabilitySummary } = response;
        if (!isVacanciesAvailable(availabilitySummary?.availabilitySummary)) {
          enqueueSnackbar(
            "No available spots for the chosen dates, please check other dates to make a reservation",
            {
              variant: "error",
              autoHideDuration: 3000,
              action: SnackbarConfig,
            },
          );
        } else {
          navigate("/reservationType", {
            state: { ...formSummary },
          });
        }
      }),
    );
  };

  useEffect(() => {
    if (
      storePersist.get(localConstant.USER_PROFILE) === "OWNER" &&
      (storePersist.get(localConstant.AGREEMENT_STATUS) === "IN_PRGRS" ||
        storePersist.get(localConstant.AGREEMENT_STATUS) === "IN_PROGESS")
    ) {
      enqueueSnackbar(
        "Esign for the agreement is still under progress. Please try again after some time !",
        {
          variant: "info",
          autoHideDuration: 3000,
          action: SnackbarConfig,
        },
      );
      return navigate("/ownerDashboard");
    } else if (
      storePersist.get(localConstant.USER_PROFILE) === "OWNER" &&
      storePersist.get(localConstant.AGREEMENT_STATUS) !== "COMPLTD" &&
      storePersist.get(localConstant.AGREEMENT_STATUS) !== "COMPLETED"
    ) {
      enqueueSnackbar(t("pages.makeReservation.text06"), {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
      return navigate("/ownerDashboard");
    } else if (
      storePersist.get(localConstant.USER_PROFILE) === "OWNER" &&
      (storePersist.get(localConstant.AGREEMENT_STATUS) === "COMPLTD" ||
        storePersist.get(localConstant.AGREEMENT_STATUS) === "COMPLETED")
    ) {
      dispatch(
        getVehicles(
          { cust_id: storePersist.get(localConstant.ENTITY_CUSTOMER_ID) },
          (data) => {
            if (data.length === 0) {
              enqueueSnackbar(t("pages.makeReservation.text07"), {
                variant: "error",
                autoHideDuration: 3000,
                action: SnackbarConfig,
              });
              if (smDown) {
                navigate("/ownerDashboard");
              } else {
                navigate("/ownerDashboard", {
                  state: {
                    openAddVehicleDialog: true,
                  },
                });
              }
            }
          },
        ),
      );
    }
  }, []);

  return (
    <div
      style={{
        width: lgUp ? `calc(100% - ${240}px)` : "100%",
        marginLeft: lgUp ? "240px" : 0,
      }}
    >
      <CustomAppBar
        title={t("pages.makeReservation.pageHeading")}
        showBackButton={true}
        onBackButtonClick={() => {
          if (state.from === "/reservation") {
            navigate("/reservation", {
              state: {
                tabType: "Book Reservation",
              },
            });
          } else {
            navigate(-1);
          }
        }}
      />
      <MenuBar />

      <Box
        sx={{
          m: lgUp ? 3.5 : 1,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          overflowY: "scroll",
          height: 600,
        }}
      >
        <Grid container direction={"column"} sx={{ mt: 2 }}>
          <Typography variant="h5" color={"primary.main"}>
            {t("pages.makeReservation.title01")}
          </Typography>
          <Typography variant="h5" color={"primary.secondary"}>
            {truncateLocationName(state?.location_display_name)}
          </Typography>
          <Typography variant="body2" color={"primary.secondary"}>
            {state?.location_address_line_1}
          </Typography>
        </Grid>
        <Stack sx={{ mt: 2 }} spacing={2}>
          <Box>
            <Typography
              fontWeight="bold"
              variant="customLable"
              color="common.inputCustomLable"
              marginBottom={1}
            >
              {t("pages.makeReservation.text01")}{" "}
              <span style={{ color: "#C50F1F" }}>*</span>
            </Typography>
            <ButtonGroup
              sx={{ boxShadow: 3, padding: 0.5, mt: 1 }}
              variant="outlined"
              aria-label="text button group"
              fullWidth
              size="large"
            >
              <Button
                sx={{
                  height: "40px",
                  borderColor: "white",
                  borderRadius: "10px",
                }}
                style={{
                  backgroundColor:
                    reservationType === "DAILY" ? "#1D5AB9" : "#fff",
                }}
                tabIndex={11}
                onClick={() => {
                  setReservationType("DAILY");
                }}
                size="medium"
              >
                <Typography
                  style={{
                    fontFamily: "Segoe UI",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 16,
                    color: reservationType === "DAILY" ? "#fff" : "#8A8886",
                  }}
                >
                  {t("pages.makeReservation.actionItem01")}
                </Typography>
              </Button>
              <Button
                sx={{
                  height: "40px",
                  borderColor: "white",
                  borderRadius: "10px",
                }}
                style={{
                  backgroundColor:
                    reservationType === "MONTHLY" ? "#1D5AB9" : "#fff",
                }}
                tabIndex={12}
                onClick={() => {
                  setReservationType("MONTHLY");
                }}
                size="medium"
              >
                <Typography
                  style={{
                    fontFamily: "Segoe UI",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 16,
                    color: reservationType === "MONTHLY" ? "#fff" : "#8A8886",
                  }}
                >
                  {t("pages.makeReservation.actionItem02")}
                </Typography>
              </Button>
            </ButtonGroup>
            {reservationType === "DAILY" ? (
              <DailyReservationForm
                handleSubmit={(data) => {
                  formSubmit(data);
                }}
                timeZone={state?.timezone}
              />
            ) : (
              <MonthlyReservationForm
                handleSubmit={(data) => {
                  formSubmit(data);
                }}
                timeZone={state?.timezone}
              />
            )}
          </Box>
        </Stack>
      </Box>
      {isLoading && <CustomLoader />}
    </div>
  );
});

export default MakeReservationPage;
